import { ImageLibraryClient } from './client';
import { ImageLibraryItem, ImageLibraryFilter, PaginatedResult } from '../types';
import { isImageFavoritedWithUserId } from './favorites';

const DEFAULT_PAGE_SIZE = 12;

/**
 * Fetch images from the library with optional filters and pagination
 */
export const fetchImageLibrary = async (filters?: ImageLibraryFilter): Promise<PaginatedResult<ImageLibraryItem>> => {
  const page = filters?.page || 0;
  const limit = filters?.limit || DEFAULT_PAGE_SIZE;
  const startIndex = page * limit;
  const endIndex = startIndex + limit - 1;
  
  let query = ImageLibraryClient.supabase
    .from('image_library')
    .select('*, image_favorites(*)', { count: 'exact' })
    // Sort by favorite count first (most favorites), then by creation date (newest)
    .order('favorite_count', { ascending: false })
    .order('created_at', { ascending: false });

  // Filter by favorites if showFavorites is true
  if (filters?.showFavorites) {
    const { data: { session } } = await ImageLibraryClient.supabase.auth.getSession();
    if (session?.user) {
      query = query.not('image_favorites', 'is', null);
    }
  }

  // Apply filters if provided - now using string comparisons instead of UUID
  if (filters) {
    if (filters.styleId) {
      query = query.eq('style_id', filters.styleId);
    }
    if (filters.influenceId) {
      query = query.eq('influence_id', filters.influenceId);
    }
    if (filters.backgroundId) {
      query = query.eq('background_id', filters.backgroundId);
    }
    if (filters.imageType) {
      query = query.eq('image_type', filters.imageType);
    }
    if (filters.environmentIds && filters.environmentIds.length > 0) {
      query = query.contains('environment_ids', filters.environmentIds);
    }
    if (filters.moodIds && filters.moodIds.length > 0) {
      query = query.contains('mood_ids', filters.moodIds);
    }
    if (filters.colorIds && filters.colorIds.length > 0) {
      query = query.contains('color_ids', filters.colorIds);
    }
    
    // Add server-side description search with keyword-based AND logic
    if (filters.description && filters.description.trim() !== '') {
      console.log('Searching for images with keyword-based description:', filters.description);
      
      // Split search query into individual keywords and trim whitespace
      const keywords = filters.description.trim().split(/\s+/).filter(keyword => keyword.length > 0);
      
      if (keywords.length > 0) {
        console.log(`🔍 Image search: Processing ${keywords.length} keyword(s):`, keywords);
        
        // Apply each keyword as a separate filter (creates AND logic between keywords)
        keywords.forEach((keyword) => {
          // Each keyword must appear in description OR original_prompt
          query = query.or(`description.ilike.%${keyword}%,original_prompt.ilike.%${keyword}%`);
        });
      }
    }
    
    // Filter by width if provided
    if (filters.width) {
      query = query.eq('width', filters.width);
    }
    
    // Filter by height if provided
    if (filters.height) {
      query = query.eq('height', filters.height);
    }
    
    // Filter by user ID if provided
    if (filters.userId) {
      query = query.eq('user_id', filters.userId);
    }
  }

  // Add pagination
  query = query.range(startIndex, endIndex);

  const { data, error, count } = await query;

  if (error) {
    console.error('Error fetching image library:', error);
    throw error;
  }

  // Process data to include isFavorite flag
  // ✅ FIX: Get session ONCE per fetch, not per image
  const { data: { session } } = await ImageLibraryClient.supabase.auth.getSession();
  const currentUserId = session?.user?.id;
  
  const processedData = await Promise.all((data || []).map(async (item: any) => {
    try {
      // ✅ FIX: Use cached userId instead of calling getSession() again
      const isFavorited = currentUserId ? await isImageFavoritedWithUserId(item.id, currentUserId) : false;
      return {
        ...item,
        is_favorite: isFavorited
      };
    } catch (error) {
      // Gracefully handle any errors and default to false
      console.warn(`[fetchImageLibrary] Failed to check favorite status for image ${item.id}:`, error);
      return {
        ...item,
        is_favorite: false
      };
    }
  }));

  // Check if there are more items to load
  const hasMore = count ? startIndex + data.length < count : false;
  
  return {
    data: processedData as ImageLibraryItem[],
    hasMore,
    total: count || undefined,
    page
  };
};
